import type {
  CollectionCreateInput,
  CollectionUpdateInput,
} from "@/modules/collections/domain/contracts";
import { Button, FormInput, FormRoot, RHF, zodResolver } from "@devhop/ui";
import type { UseFormReturn } from "react-hook-form";
import { useCallback, useEffect } from "react";
import { z } from "zod";

const CollectionFormSchema = z.object({
  nameLo: z.string().min(1, "ຕ້ອງໃສ່ຊື່ (ລາວ)"),
  nameTh: z.string().optional(),
  nameEn: z.string().optional(),
  slug: z.string().min(1, "ຕ້ອງໃສ່ Slug"),
  sortOrder: z.coerce.number().int().min(0).default(0),
});

export type CollectionFormValues = z.output<typeof CollectionFormSchema>;

const DEV_FILL: CollectionFormValues = {
  nameLo: "ເສື້ອຜ້າ",
  nameTh: "เสื้อผ้า",
  nameEn: "Clothing",
  slug: "clothing",
  sortOrder: 0,
};

type CollectionFormProps = {
  initialValues?: Partial<CollectionFormValues>;
  onSubmit: (values: CollectionCreateInput | CollectionUpdateInput) => void;
  onCancel?: () => void;
  submitting?: boolean;
  onDevFill?: () => void;
};

export function CollectionForm({
  initialValues,
  onSubmit,
  onCancel,
  submitting = false,
  onDevFill,
}: CollectionFormProps) {
  const methods = RHF.useForm<CollectionFormValues>({
    resolver: zodResolver(CollectionFormSchema) as any,
    defaultValues: {
      nameLo: "",
      nameTh: "",
      nameEn: "",
      slug: "",
      sortOrder: 0,
      ...initialValues,
    },
  });

  const fillDev = () => {
    methods.reset(DEV_FILL);
  };

  // Auto-generate slug from English name
  const generateSlug = useCallback((text: string) => {
    return text
      .toLowerCase()
      .replace(/[^\w\s-]/g, "") // Remove special characters
      .replace(/[\s_-]+/g, "-") // Replace spaces and underscores with hyphens
      .replace(/^-+|-+$/g, ""); // Remove leading/trailing hyphens
  }, []);

  // Watch for changes in nameEn to auto-generate slug
  const nameEn = methods.watch("nameEn");
  useEffect(() => {
    if (nameEn && !initialValues?.slug) {
      const slug = generateSlug(nameEn);
      if (slug !== methods.getValues("slug")) {
        methods.setValue("slug", slug);
      }
    }
  }, [nameEn, generateSlug, methods, initialValues?.slug]);

  return (
    <FormRoot<CollectionFormValues>
      methods={methods as UseFormReturn<CollectionFormValues>}
      onSubmit={(vals) =>
        onSubmit({
          name: { lo: vals.nameLo, th: vals.nameTh, en: vals.nameEn },
          slug: vals.slug,
          sortOrder: vals.sortOrder,
        })
      }
    >
      <div className="space-y-6" data-tourid="collection-form">
        <div className="grid grid-cols-1 gap-4">
          <FormInput
            name="nameLo"
            label="ຊື່ (ລາວ)"
            placeholder="ໃສ່ຊື່ເປັນພາສາລາວ"
            required
            data-tourid="collection-form-nameLo"
          />
          <FormInput
            name="nameTh"
            label="ຊື່ (ໄທ)"
            placeholder="ໃສ່ຊື່ເປັນພາສາໄທ"
          />
          <FormInput
            name="nameEn"
            label="ຊື່ (ອັງກິດ)"
            placeholder="Enter name in English"
            data-tourid="collection-form-nameEn"
          />
          <FormInput
            name="slug"
            label="Slug"
            placeholder="collection-slug"
            required
            readOnly
            hint="URL-friendly identifier (auto-generated from English name)"
            data-tourid="collection-form-slug"
          />
          <FormInput
            name="sortOrder"
            label="ລຳດັບການສະແດງ"
            type="number"
            placeholder="0"
            data-tourid="collection-form-sortOrder"
          />
        </div>

        <div className="flex items-center justify-between">
          <div className="flex gap-2" data-tourid="collection-form-submit">
            <Button type="submit" disabled={submitting}>
              {submitting ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກ"}
            </Button>
            {onCancel && (
              <Button type="button" variant="outline" onClick={onCancel}>
                ຍົກເລີກ
              </Button>
            )}
          </div>
          {onDevFill && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                fillDev();
                onDevFill();
              }}
            >
              DEV Fill
            </Button>
          )}
        </div>
      </div>
    </FormRoot>
  );
}
